/* ============================================================
   TROLI MARKETPLACE  (/marketplace/troli)  —  v3.9
   ============================================================
   ⭐ Troli dalam pelayar hanya menyimpan `productId` + `qty`
      (lihat services/cartStore.js). Halaman ini membaca SEMULA
      setiap produk daripada Firestore setiap kali dibuka, jadi
      harga, stok dan status yang dipapar sentiasa yang terkini —
      bukan gambaran lama yang tersimpan entah bila.

   ⛔ Jumlah yang dipapar di sini ialah untuk MATA PELANGGAN sahaja.
      Server mengira semula harga setiap barang semasa bil dicipta.
      Jangan sekali-kali menghantar jumlah dari sini.

   ⚠️ Harga daripada Firestore dalam SEN. Guna `formatPrice`.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShieldCheck, ShoppingCart, Trash2, TriangleAlert } from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, Card, EmptyState, PageHeader } from '../design/ui';
import { IKON_KATEGORI, IKON_LAIN } from './marketplaceIcons';
import { auth } from '../firebase';
import { getProduct, formatPrice, categoryLabel, createMarketplaceCartBill } from '../services/marketplaceService';
import { bacaTroli, tetapKuantiti, buangDariTroli, kosongkanTroli, HAD_TROLI } from '../services/cartStore';
import { toast } from './toastBus';
import { useT } from '../i18n';

/* Pemutar kuantiti. Butang tolak/tambah sahaja — medan taip dibuang
   dengan sengaja: ia menjemput nilai pelik ("2e3", "-1", "abc") pada
   kawalan yang menentukan berapa banyak duit dicaj. */
export function Kuantiti({ nilai, maks, kunci, onUbah, t }) {
    if (kunci) {
        return <span className="s-hint" style={{ whiteSpace: 'nowrap' }}>{t('market.cart.rentalOne')}</span>;
    }
    const bolehTambah = !(typeof maks === 'number' && nilai >= maks);
    const kotak = {
        display: 'grid', placeItems: 'center', width: 28, height: 28,
        borderRadius: 8, border: '1px solid var(--s-line)', background: 'var(--s-card)',
        color: 'var(--s-ink)', cursor: 'pointer', flexShrink: 0,
    };
    const mati = { ...kotak, opacity: .4, cursor: 'not-allowed' };
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button type="button" style={nilai <= 1 ? mati : kotak} disabled={nilai <= 1}
                aria-label={t('market.cart.less')} onClick={() => onUbah(nilai - 1)}>
                <Minus size={14} strokeWidth={2} />
            </button>
            <span style={{
                minWidth: 22, textAlign: 'center', fontSize: 14, fontWeight: 600,
                color: 'var(--s-ink)', fontVariantNumeric: 'tabular-nums',
            }}>{nilai}</span>
            <button type="button" style={bolehTambah ? kotak : mati} disabled={!bolehTambah}
                aria-label={t('market.cart.more')} onClick={() => onUbah(nilai + 1)}>
                <Plus size={14} strokeWidth={2} />
            </button>
        </div>
    );
}

export default function TroliV39View() {
    const navigate = useNavigate();
    const t = useT();

    const [baris, setBaris] = useState([]);      // { productId, qty, produk, sebabMati }
    const [memuat, setMemuat] = useState(true);
    const [sibuk, setSibuk] = useState(false);
    const [whatsapp, setWhatsapp] = useState('');

    /* Muat semula produk sebenar untuk setiap barang dalam troli.
       Produk yang sudah dipadam / ditarik balik / habis stok TIDAK dibuang
       sendiri — ia dipapar sebagai baris mati dengan sebabnya. Membuangnya
       secara senyap akan membuat pelanggan tertanya-tanya ke mana perginya
       barang yang dia pilih. */
    const muat = useCallback(async () => {
        const troli = bacaTroli();
        if (!troli.length) { setBaris([]); setMemuat(false); return; }
        try {
            const hasil = await Promise.all(troli.map(async (b) => {
                let produk = null;
                try { produk = await getProduct(b.productId); } catch { produk = null; }
                let sebabMati = null;
                if (!produk) sebabMati = 'tiada';
                else if (produk.status === 'sold_out' || produk.stock === 0) sebabMati = 'habis';
                else if (produk.status !== 'published') sebabMati = 'tarik';
                else if (produk.pricingMode === 'free') sebabMati = 'percuma';
                return { ...b, produk, sebabMati };
            }));
            setBaris(hasil);
        } catch (e) {
            toast.error(t('market.cart.loadFailed', { ralat: e?.message || e }));
        } finally {
            setMemuat(false);
        }
    }, [t]);

    /* Dimuat SEKALI semasa halaman dibuka. Fungsi muat sengaja TIDAK dimasukkan
       dalam senarai kebergantungan: ia bergantung pada kamus bahasa, yang bertukar apabila
       bahasa bertukar — menambahnya di sini akan mencetuskan satu pusingan bacaan
       Firestore penuh setiap kali pengguna menukar bahasa. Corak sama seperti
       MarketplaceV39View. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { muat(); }, []);

    function ubahKuantiti(productId, n) {
        tetapKuantiti(productId, n);
        setBaris((lama) => lama
            .map((b) => (b.productId === productId ? { ...b, qty: n } : b))
            .filter((b) => b.qty >= 1));
    }
    function buang(productId) {
        buangDariTroli(productId);
        setBaris((lama) => lama.filter((b) => b.productId !== productId));
    }

    const hidup = baris.filter((b) => !b.sebabMati);
    const mati = baris.filter((b) => b.sebabMati);
    const jumlah = hidup.reduce((n, b) => n + (Number(b.produk?.price) || 0) * b.qty, 0);
    const unit = hidup.reduce((n, b) => n + b.qty, 0);

    /* Server yang mencipta pesanan + bil (harga dibaca di sana), kemudian
       pelanggan dipindahkan ke Billplz. Webhook yang melepaskan akses selepas
       bayaran sah — tiada langkah admin di antaranya.

       Troli dikosongkan SEBELUM berpindah: pesanan itu sudah wujud dan boleh
       disambung bayarannya daripada "Pembelian Saya" kalau pelanggan berpatah
       balik, jadi membiarkan troli berisi hanya menjemput dia membayar dua kali
       untuk barang yang sama.
       ⚠️ `setSibuk(false)` sengaja TIDAK dipanggil pada laluan berjaya: halaman
          sedang berpindah, dan butang yang hidup semula mengundang klik kedua. */
    async function bayar() {
        if (!auth.currentUser) { toast.error(t('market.cart.loginFirst')); return; }
        if (!hidup.length) return;
        setSibuk(true);
        try {
            const { url } = await createMarketplaceCartBill(
                hidup.map((b) => ({ productId: b.productId, qty: b.qty })),
                { whatsapp: whatsapp.trim() },
            );
            kosongkanTroli();
            window.location.href = url;
        } catch (e) {
            toast.error(t('market.cart.payFailed', { ralat: e?.message || e }));
            setSibuk(false);
            muat();   // stok mungkin berubah — papar keadaan terkini
        }
    }

    const kembali = (
        <Btn variant="quiet" size="sm" icon={<ArrowLeft size={15} strokeWidth={2} />}
            onClick={() => navigate('/marketplace')} style={{ marginBottom: 4 }}>
            {t('market.cart.back')}
        </Btn>
    );

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px', maxWidth: 980, margin: '0 auto' }}>
            {kembali}
            <PageHeader title={t('market.cart.title')} subtitle={t('market.cart.subtitle')} />

            {memuat ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[1, 2].map((i) => <div key={i} className="s-skel" style={{ height: 92, borderRadius: 'var(--s-r-lg)' }} />)}
                </div>
            ) : !baris.length ? (
                <EmptyState
                    title={t('market.cart.emptyTitle')}
                    action={<Btn onClick={() => navigate('/marketplace')}>{t('market.cart.browse')}</Btn>}
                >
                    {t('market.cart.emptyDesc')}
                </EmptyState>
            ) : (
                <div className="troli39" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 20, alignItems: 'start' }}>

                    {/* ─── Senarai barang ─── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {baris.map((b) => {
                            const p = b.produk;
                            const Ikon = IKON_KATEGORI[p?.category] || IKON_LAIN;
                            const stok = typeof p?.stock === 'number' ? p.stock : null;
                            const sewa = Number(p?.durationDays) > 0;
                            const maks = sewa ? 1 : Math.min(HAD_TROLI.kuantiti, stok ?? HAD_TROLI.kuantiti);
                            return (
                                <Card key={b.productId} style={{
                                    padding: 14, display: 'flex', gap: 14, alignItems: 'flex-start',
                                    opacity: b.sebabMati ? .6 : 1,
                                }}>
                                    <div style={{
                                        width: 62, height: 62, borderRadius: 10, flexShrink: 0,
                                        border: '1px solid var(--s-line)', display: 'grid', placeItems: 'center',
                                        background: p?.imageUrl
                                            ? `var(--s-fill) center/cover no-repeat url(${JSON.stringify(p.imageUrl)})`
                                            : 'var(--s-fill)',
                                    }}>
                                        {!p?.imageUrl && <Ikon size={22} strokeWidth={1.4} style={{ color: 'var(--s-ink-3)' }} />}
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', lineHeight: 1.35 }}>
                                            {p?.title || t('market.cart.gone')}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 5 }}>
                                            {p?.category && <span className="s-hint">{categoryLabel(p.category)}</span>}
                                            {!b.sebabMati && <span className="s-hint">{formatPrice(p.price)} / unit</span>}
                                            {!b.sebabMati && stok !== null && stok <= 5 && (
                                                <Badge tone="warn">{t('market.cart.stockLeft', { n: stok })}</Badge>
                                            )}
                                            {b.sebabMati && (
                                                <Badge tone="stop">
                                                    {b.sebabMati === 'habis' ? t('market.cart.soldOut')
                                                        : b.sebabMati === 'tiada' ? t('market.cart.gone')
                                                            : b.sebabMati === 'percuma' ? t('market.cart.isFree')
                                                                : t('market.cart.withdrawn')}
                                                </Badge>
                                            )}
                                        </div>

                                        {!b.sebabMati && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 11 }}>
                                                <Kuantiti nilai={b.qty} maks={maks} kunci={sewa} t={t}
                                                    onUbah={(n) => ubahKuantiti(b.productId, n)} />
                                                <span style={{
                                                    fontSize: 15, fontWeight: 600, color: 'var(--s-ink)',
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}>
                                                    {formatPrice((Number(p.price) || 0) * b.qty)}
                                                </span>
                                                {!sewa && stok !== null && b.qty >= stok && (
                                                    <span className="s-hint">{t('market.cart.maxStock')}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <button type="button" aria-label={t('market.cart.remove')}
                                        onClick={() => buang(b.productId)}
                                        style={{
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: 'var(--s-ink-3)', padding: 4, flexShrink: 0,
                                        }}>
                                        <Trash2 size={16} strokeWidth={1.9} />
                                    </button>
                                </Card>
                            );
                        })}
                    </div>

                    {/* ─── Ringkasan + bayar ─── */}
                    <Card style={{ padding: 18, position: 'sticky', top: 18 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', marginBottom: 12 }}>
                            {t('market.cart.summary')}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                            <span className="s-hint">{t('market.cart.units', { n: unit })}</span>
                        </div>
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                            paddingTop: 10, marginTop: 6, borderTop: '1px solid var(--s-line)',
                        }}>
                            <span style={{ fontSize: 14, color: 'var(--s-ink-2)' }}>{t('market.cart.total')}</span>
                            <span style={{
                                fontSize: 22, fontWeight: 600, color: 'var(--s-ink)',
                                fontVariantNumeric: 'tabular-nums',
                            }}>{formatPrice(jumlah)}</span>
                        </div>

                        {mati.length > 0 && (
                            <div style={{
                                display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 14,
                                padding: '10px 12px', borderRadius: 10,
                                background: 'var(--s-fill)', border: '1px solid var(--s-line)',
                            }}>
                                <TriangleAlert size={15} strokeWidth={2} style={{ color: 'var(--s-warn)', flexShrink: 0, marginTop: 1 }} />
                                <span className="s-hint">{t('market.cart.skipNote', { n: mati.length })}</span>
                            </div>
                        )}

                        <div style={{ marginTop: 16, marginBottom: 14 }}>
                            <div className="s-lbl">{t('market.cart.whatsapp')}</div>
                            <input className="s-input" value={whatsapp}
                                onChange={(e) => setWhatsapp(e.target.value)}
                                placeholder={t('market.cart.whatsappPh')} />
                        </div>

                        <Btn disabled={sibuk || !hidup.length} onClick={bayar}
                            icon={<ShieldCheck size={16} strokeWidth={2} />} style={{ width: '100%' }}>
                            {sibuk ? t('market.cart.preparing') : t('market.cart.checkout', { jumlah: formatPrice(jumlah) })}
                        </Btn>

                        <p className="s-hint" style={{ margin: '12px 0 0' }}>{t('market.cart.payNote')}</p>
                    </Card>
                </div>
            )}

            <style>{`
                @media (max-width: 820px) {
                    .troli39 { grid-template-columns: 1fr !important; }
                    .troli39 > :last-child { position: static !important; }
                }
            `}</style>
        </div>
    );
}

/* Ikon troli + lencana bilangan. Dipakai pada kepala halaman Marketplace
   dan halaman produk, jadi kedua-duanya mustahil terpesong. */
export function ButangTroli({ onNavigate }) {
    const t = useT();
    const [n, setN] = useState(0);
    useEffect(() => {
        // Import malas: fail ini dimuat oleh halaman produk juga, dan kita mahu
        // langganan dipasang sekali sahaja setiap komponen yang memakainya.
        let henti = () => {};
        import('../services/cartStore').then((m) => {
            setN(m.jumlahUnit());
            henti = m.langganTroli(() => setN(m.jumlahUnit()));
        });
        return () => henti();
    }, []);
    return (
        <Btn variant="ghost" icon={<ShoppingCart size={15} strokeWidth={2} />}
            onClick={() => onNavigate?.('/marketplace/troli')}>
            {t('market.cart.title')}
            {n > 0 && (
                <span style={{
                    marginLeft: 7, minWidth: 18, height: 18, padding: '0 5px',
                    display: 'inline-grid', placeItems: 'center', borderRadius: 999,
                    background: 'var(--s-primary)', color: 'var(--s-primary-ink)',
                    fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                }}>{n}</span>
            )}
        </Btn>
    );
}
